import React, { useEffect, useState } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Button, Text, Flex, SimpleGrid
} from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import { useTableDensity } from '../context/TableDensityContext';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import { useParams, useNavigate } from 'react-router-dom';
import SummaryCard from '../components/SummaryCard';
// removed collapsible icons in simplified view
import StatusBadge from '../components/ui/StatusBadge';
import client from '../utils/fetchClient';
import HelpTooltip from '../components/ui/HelpTooltip';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function useRunDetail(apiKey, runId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!apiKey || !runId) return;
    setLoading(true);
    setError(null);
    client.get(`${API_BASE_URL}/api/test-runs/${runId}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Error fetching run details');
        setLoading(false);
      });
  }, [apiKey, runId]);

  return { data, loading, error };
}



export default function RunDetailPage() {
  const { density } = useTableDensity();
  const tableSize = density === 'compact' ? 'sm' : 'md';
  const apiKey = process.env.REACT_APP_API_KEY;
  const { runId } = useParams();
  const { data, loading, error } = useRunDetail(apiKey, runId);
  const navigate = useNavigate();
  // removed expanded rows in simplified view

  if (loading) return <LoadingState title="Loading run details" minH="60vh" />;
  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!data || !data.testRun) return <ErrorState message="Test run not found." />;

  const { testRun, testCases } = data;

  // collapse toggling removed in simplified view

  return (
    <Page>
      <Flex mb={4} fontSize="sm" color="gray.600" gap={2} align="center">
        <Button variant="link" size="sm" onClick={() => navigate('/test-runs')}>Test Runs</Button>
        <Text>/</Text>
        <Text fontWeight="semibold">Run #{testRun.id}</Text>
      </Flex>
      <SectionCard mb={6}>
        {/* Header */}
        <Flex align="center" mb={6} gap={4} wrap="wrap">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm">Back</Button>
          <Text fontSize="2xl" fontWeight="bold" flexShrink={0}>{testRun.name || `Test Run #${testRun.id}`}</Text>
          <StatusBadge status={testRun.status} />
        </Flex>

        {/* Metadata Grid */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4} mb={6}>
          <Box><Text fontWeight="medium">Run ID</Text><Text>{testRun.id}</Text></Box>
          <Box><Text fontWeight="medium">User</Text><Text>{testRun.userId}</Text></Box>
          <Box><Text fontWeight="medium">Project</Text><Text>{testRun.projectId}</Text></Box>
          <Box><Text fontWeight="medium">Start Time</Text><Text>{testRun.startTime ? new Date(testRun.startTime).toLocaleString() : '-'}</Text></Box>
          <Box><Text fontWeight="medium">End Time</Text><Text>{testRun.endTime ? new Date(testRun.endTime).toLocaleString() : '-'}</Text></Box>
        </SimpleGrid>

        {/* Summary Cards */}
        <Flex gap={4} wrap="wrap">
          <SummaryCard label="Total Tests" value={testRun.totalTests} color="gray" />
          <SummaryCard label="Passed" value={testRun.passedTests} color="green" />
          <SummaryCard label="Failed" value={testRun.failedTests} color="red" />
          <SummaryCard label="Skipped" value={testRun.skippedTests} color="yellow" />
          <SummaryCard label="Duration (ms)" value={testRun.totalDuration} color="blue" />
        </Flex>
      </SectionCard>

      {/* Test Cases Section */}
      <SectionCard>
        <Text fontSize="xl" fontWeight="bold" mb={4} display="flex" alignItems="center">Test Cases <HelpTooltip label="All test cases in this run with status, duration, and errors. Use Compare to see changes between runs." href="https://docs.example.com/qa/run-detail" /></Text>
        <Box overflowX="auto">
          <Table display={{ base: 'none', md: 'table' }} variant="striped" size={tableSize} borderWidth={1} borderColor="gray.200" sx={{ th: { position: 'sticky', top: 0, bg: 'white', zIndex: 1 } }}>
            <Thead>
              <Tr>
                <Th borderWidth={1} borderColor="gray.200">Title</Th>
                <Th borderWidth={1} borderColor="gray.200">Status</Th>
                <Th borderWidth={1} borderColor="gray.200">Duration (ms)</Th>
                <Th borderWidth={1} borderColor="gray.200">Error Message</Th>
              </Tr>
            </Thead>
            <Tbody>
              {testCases && testCases.length > 0 ? testCases.map(tc => (
                <Tr key={tc.id}>
                  <Td borderWidth={1} borderColor="gray.200">{tc.title}</Td>
                  <Td borderWidth={1} borderColor="gray.200"><StatusBadge status={tc.status} /></Td>
                  <Td borderWidth={1} borderColor="gray.200">{tc.duration}</Td>
                  <Td borderWidth={1} borderColor="gray.200">{tc.errorMessage || '-'}</Td>
                </Tr>
              )) : (
                <Tr>
                  <Td colSpan={4} borderWidth={1} borderColor="gray.200"><Text>No test cases found for this run.</Text></Td>
                </Tr>
              )}
            </Tbody>
          </Table>
          {/* Mobile stacked cards */}
          <Box display={{ base: 'block', md: 'none' }}>
            {testCases && testCases.length > 0 ? testCases.map(tc => (
              <Box key={tc.id} borderWidth={1} borderColor="gray.200" borderRadius="lg" p={3} mb={3} bg="white">
                <Text fontWeight="bold" mb={1}>{tc.title}</Text>
                <Flex justify="space-between" fontSize="sm" mb={1}><Text color="gray.500">Status</Text><StatusBadge status={tc.status} /></Flex>
                <Flex justify="space-between" fontSize="sm" mb={1}><Text color="gray.500">Duration</Text><Text>{tc.duration}</Text></Flex>
                <Box mt={2}>
                  <Text color="gray.500" fontSize="sm" mb={1}>Error Message</Text>
                  <Text fontSize="sm" color={tc.errorMessage ? 'red.600' : 'gray.400'} noOfLines={3}>{tc.errorMessage || '-'}</Text>
                </Box>
              </Box>
            )) : (
              <Text>No test cases found for this run.</Text>
            )}
          </Box>
        </Box>
      </SectionCard>
    </Page>
  );
} 